<div class="assessment-form-container">
  @if (loading) {
    <div class="loading-overlay">
      <mat-spinner diameter="60"></mat-spinner>
      <p>Loading assessment...</p>
    </div>
  } @else if (loadError) {
    <div class="loading-overlay">
      <mat-icon class="error-icon">error_outline</mat-icon>
      <p>{{ loadError }}</p>
      <button mat-stroked-button (click)="goBack()">Back</button>
    </div>
  } @else {
    <mat-sidenav-container class="sidenav-container">
      <mat-sidenav #actionsSidenav mode="side" opened position="start" class="actions-sidenav">
        <app-entity-actions-sidenav
          [title]="isEditMode ? 'Edit Assessment' : 'New Assessment'"
          [actions]="sidenavActions">
        </app-entity-actions-sidenav>
      </mat-sidenav>

      <mat-sidenav-content class="form-content">
        <div class="header-bar">
          <button mat-button (click)="goBack()">
            <mat-icon>arrow_back</mat-icon>
            Back
          </button>
          <h2>{{ isEditMode ? 'Edit Assessment' : 'New Assessment' }}</h2>
        </div>

        @if (isViewer()) {
          <div class="notice notice-info">
            <mat-icon>visibility</mat-icon>
            Your role is Viewer, so you can look at this assessment but not save changes.
          </div>
        }

        <form [formGroup]="form" class="form-body">

          <!-- Details -->
          <mat-card class="form-card">
            <mat-card-header>
              <mat-card-title>Assessment Details</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Name</mat-label>
                <input matInput formControlName="name" required>
                @if (form.get('name')?.hasError('required')) {
                  <mat-error>Name is required</mat-error>
                }
              </mat-form-field>

              <div class="form-row two-col">
                <mat-form-field appearance="outline">
                  <mat-label>Type</mat-label>
                  <mat-select formControlName="type" (selectionChange)="onTypeSelected($event.value)">
                    @for (type of assessmentTypes; track type) {
                      <mat-option [value]="type">{{ type }}</mat-option>
                    }
                  </mat-select>
                  @if (isEditMode) {
                    <mat-hint>The type can't change once an assessment is created.</mat-hint>
                  }
                </mat-form-field>

                <mat-form-field appearance="outline">
                  <mat-label>Critical Date</mat-label>
                  <input matInput [matDatepicker]="datePicker" formControlName="criticalDate">
                  <mat-datepicker-toggle matIconSuffix [for]="datePicker"></mat-datepicker-toggle>
                  <mat-datepicker #datePicker></mat-datepicker>
                </mat-form-field>
              </div>

              <div class="form-row three-col">
                @for (p of [
                  { field: 'owner', label: 'Owner' },
                  { field: 'assessor', label: 'Assessor' },
                  { field: 'finalReviewer', label: 'Final Reviewer' }
                ]; track p.field) {
                  <mat-form-field appearance="outline">
                    <mat-label>{{ p.label }}</mat-label>
                    <input matInput
                           [formControlName]="p.field"
                           [matAutocomplete]="auto"
                           (focus)="filterPeople($any(p.field), form.get(p.field)?.value)"
                           (input)="filterPeople($any(p.field), $any($event.target).value)"
                           (blur)="onPersonBlur($any(p.field))"
                           placeholder="Start typing a name">
                    <mat-autocomplete #auto="matAutocomplete" [displayWith]="displayPerson">
                      @for (person of filteredPeople[$any(p.field)]; track person.email) {
                        <mat-option [value]="person">{{ person.friendlyName }}</mat-option>
                      }
                    </mat-autocomplete>
                  </mat-form-field>
                }
              </div>

              <div class="form-row two-col">
                <mat-form-field appearance="outline">
                  <mat-label>Source Record</mat-label>
                  <input matInput [value]="sourceRecord" readonly>
                </mat-form-field>

                <mat-form-field appearance="outline">
                  <mat-label>Status</mat-label>
                  <mat-select formControlName="status">
                    @for (status of assessmentStatuses; track status) {
                      <mat-option [value]="status">{{ status }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </div>
            </mat-card-content>
          </mat-card>

          <!-- Quality Metrics -->
          <mat-card class="form-card">
            <mat-card-header>
              <mat-card-title>Quality Metrics</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Quality Metrics Contract Location</mat-label>
                <input matInput formControlName="qualityMetricsLocation">
              </mat-form-field>
              <label class="editor-label">Quality Metrics Contract Language Excerpt</label>
              <p-editor formControlName="qualityMetricsLanguageExcerpt" [style]="{ height: '160px' }">
                <ng-template #header>
                  <span class="ql-formats">
                    <button type="button" class="ql-bold" aria-label="Bold"></button>
                    <button type="button" class="ql-italic" aria-label="Italic"></button>
                    <button type="button" class="ql-underline" aria-label="Underline"></button>
                  </span>
                </ng-template>
              </p-editor>
            </mat-card-content>
          </mat-card>

          <!-- Evaluation Instructions -->
          @if (instructionsHtml) {
            <mat-expansion-panel class="form-card instructions-panel">
              <mat-expansion-panel-header>
                <mat-panel-title>
                  <mat-icon>info</mat-icon>
                  Evaluation Instructions
                </mat-panel-title>
              </mat-expansion-panel-header>
              <div class="instructions" [innerHTML]="instructionsHtml"></div>
            </mat-expansion-panel>
          }

          <!-- Questions & Scoring -->
          <mat-card class="form-card">
            <mat-card-header>
              <mat-card-title>
                Questions
                <span class="total-score">Total Score: {{ totalScore }}</span>
              </mat-card-title>
            </mat-card-header>
            <mat-card-content>
              @if (questionsFormArray.length === 0) {
                <p class="muted">
                  @if (form.get('type')?.value) {
                    No questions are set up for this type yet.
                  } @else {
                    Pick a Type to load its questions.
                  }
                </p>
              }
              <div formArrayName="questionsAndAnswers">
                @for (qa of questionsFormArray.controls; track $index; let i = $index) {
                  <div class="question-row" [formGroupName]="i">
                    <div class="question-text">{{ qa.get('question')?.value }}</div>
                    <div class="question-controls">
                      <mat-form-field appearance="outline" class="score-field">
                        <mat-label>Score</mat-label>
                        <mat-select formControlName="score">
                          @for (choice of scoreChoices; track choice.description) {
                            <mat-option [value]="choice.score">{{ choice.description }}</mat-option>
                          }
                        </mat-select>
                      </mat-form-field>
                      <mat-form-field appearance="outline" class="comment-field">
                        <mat-label>Comment</mat-label>
                        <textarea matInput formControlName="comment" rows="2"></textarea>
                      </mat-form-field>
                    </div>
                  </div>
                  @if (!$last) {
                    <mat-divider></mat-divider>
                  }
                }
              </div>
            </mat-card-content>
          </mat-card>

          <!-- Recommendations -->
          <mat-card class="form-card">
            <mat-card-header>
              <mat-card-title>Recommendations</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <p-editor formControlName="recommendation" [style]="{ height: '180px' }">
                <ng-template #header>
                  <span class="ql-formats">
                    <button type="button" class="ql-bold" aria-label="Bold"></button>
                    <button type="button" class="ql-italic" aria-label="Italic"></button>
                    <button type="button" class="ql-underline" aria-label="Underline"></button>
                  </span>
                </ng-template>
              </p-editor>
            </mat-card-content>
          </mat-card>

          @if (saveError) {
            <div class="notice notice-error">
              <mat-icon>error</mat-icon>
              <span>{{ saveError }}</span>
              @if (conflict) {
                <button mat-stroked-button type="button" (click)="reload()">Reload</button>
              }
            </div>
          }

          <div class="form-actions">
            <button mat-raised-button color="primary" type="button" (click)="onSubmit()"
                    [disabled]="saving || conflict || isViewer()">
              @if (saving) {
                <mat-spinner diameter="20"></mat-spinner>
              } @else {
                <mat-icon>save</mat-icon>
                Save Assessment
              }
            </button>
            <button mat-stroked-button type="button" (click)="goBack()">Cancel</button>
          </div>

          @if (attestationText) {
            <p class="attestation-text">{{ attestationText }}</p>
          }
        </form>
      </mat-sidenav-content>
    </mat-sidenav-container>
  }
</div>
